import { markRaw } from 'vue'
import { cloneDeep, isEqual, omit } from 'lodash'

import { ModalEvent } from '@types'
import { Asset, AssetPrice, AssetSecurityInfo } from './utils/types'
import { Entity } from '../utils/common'
import { EntityEvent } from '../utils/enums'
import { EntityErrors } from '../utils/types'

import {
  ASSET_ACCOUNT_INVESTMENT,
  ASSET_ACCOUNT_PRE_CREATED,
  ASSET_ACCOUNT_TRANSFER,
  ASSET_CURRENCY_TYPE,
  ASSET_DEFAULT_DATA,
  ASSET_FIELD,
} from './utils/const'
import { AssetCategoryAccount } from './utils/enums'

import { Rule, rules } from '@/helpers/validate'
import { getCurrencySymbol } from '@/helpers/common'

import { useAssetsStore } from '@/store/assets/index'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useTagsBunchStore } from '@/store/tags/bunch'

import AssetDialog from './components/AssetDialog.vue'
import AssetFormType from './components/AssetFormType.vue'
import AssetFormName from './components/AssetFormName.vue'
import AssetFormTicker from './components/AssetFormTicker.vue'
import AssetFormCurrency from './components/AssetFormCurrency.vue'
import AssetFormDescription from './components/AssetFormDescription.vue'
import AssetDrawer from './components/AssetDrawer.vue'

import CurrencyDialog from './components/CurrencyDialog.vue'
import CurrencyDrawer from './components/CurrencyDrawer.vue'

import AccountDialog from './components/AccountDialog.vue'
import AccountDrawer from './components/AccountDrawer.vue'
import AccountFormCategory from './components/AccountFormCategory.vue'
import AccountFormSubcategory from './components/AccountFormSubcategory.vue'

import AccountConnector from './components/AccountConnector.vue'

export class AssetClass extends Entity<Asset> {
  private assetsStore
  private bunchStore
  private modalsStore

  private priceList?: AssetPrice[] = undefined

  private securityInfoValue?: AssetSecurityInfo

  private thumbnailDocumentUrl: string | null = null

  constructor(data: Partial<Asset>, created = false) {
    if (data.description === null) {
      data.description = ''
    }
    super(data, ASSET_DEFAULT_DATA, created)
    this.assetsStore = useAssetsStore()
    this.bunchStore = useAssetsBunchStore()
    this.modalsStore = useModalsStore()
  }

  get isChanged() {
    if (this.frozen) return false
    const omitFields = [
      ASSET_FIELD.CONNECTOR_STATUS,
      ASSET_FIELD.CONNECTOR_STATUS_MESSAGE,
      ASSET_FIELD.LINKED_ACCOUNT_ID
    ]

    const data = omit(this.data, omitFields)
    const original = omit(this.original, omitFields)

    return !isEqual(data, original)
  }

  get isCommon() {
    return !this.isCurrency && !this.isAccount
  }

  get isCurrency() {
    return this.field(ASSET_FIELD.TYPE).value === ASSET_CURRENCY_TYPE
  }

  get isExpenseAccount() {
    return this.field(ASSET_FIELD.TYPE).value === AssetCategoryAccount.EXPENSE
  }

  get isIncomeAccount() {
    return this.field(ASSET_FIELD.TYPE).value === AssetCategoryAccount.INCOME
  }

  get isAccount() {
    return this.isExpenseAccount || this.isIncomeAccount
  }

  get isPrecreatedAccount() {
    if (!this.isAccount) return false
    const name = this.field<string>(ASSET_FIELD.NAME).value?.toLowerCase()
    return ASSET_ACCOUNT_PRE_CREATED.includes(name)
  }

  get isInvestmentAccount() {
    if (!this.isAccount) return false
    const name = this.field<string>(ASSET_FIELD.NAME).value?.toLowerCase()
    return ASSET_ACCOUNT_INVESTMENT.includes(name)
  }

  get isTransferAccount() {
    if (!this.isAccount) return false
    const name = this.field<string>(ASSET_FIELD.NAME).value?.toLowerCase()
    return ASSET_ACCOUNT_TRANSFER.includes(name)
  }

  get isReadonly() {
    return this.isAccount && this.isPrecreatedAccount
  }

  get isDisabled() {
    return this.isReadonly
  }

  get isLinked() {
    return !!this.field<string | null>(ASSET_FIELD.LINKED_ACCOUNT_ID).value
  }

  get thumbnailId() {
    return this.data.thumbnail || undefined
  }

  set thumbnailId(value: string | undefined) {
    this.data.thumbnail = value
  }

  get thumbnailUrl() {
    return this.thumbnailDocumentUrl
  }

  set thumbnailUrl(value: string | null) {
    this.thumbnailDocumentUrl = value
  }

  get docs() {
    return this.field<string[]>(ASSET_FIELD.DOCS).value
  }

  set docs(value: string[]) {
    this.field<string[]>(ASSET_FIELD.DOCS).value = value
  }

  get getModal() {
    if (this.isCurrency) {
      return markRaw(this.isDialog ? CurrencyDialog : CurrencyDrawer)
    }
    if (this.isAccount) {
      return markRaw(this.isDialog ? AccountDialog : AccountDrawer)
    }
    return markRaw(this.isDialog ? AssetDialog : AssetDrawer)
  }

  openDrawer(
    path?: string[],
    amount?: number,
    callback?: () => void,
    isLeaf = false,
  ) {
    let modalInstance: any
    if (this.isAccount) {
      modalInstance = this.modalsStore.init(this.id, this, { allowTray: true })
      modalInstance?.open(this.modalsStore.getZIndex())
    } else {
      modalInstance = this.modalsStore.init(this.id, this, { allowTray: true })
      modalInstance?.open(this.modalsStore.getZIndex(), {
        instance: this,
        amount,
        path,
        isLeaf,
      })
    }
    callback && modalInstance?.addEventListener(ModalEvent.CLOSE, callback)
  }

  openConnectorInfo(assetId?: string, callback?: () => void) {
    const name = this.field<string>(ASSET_FIELD.NAME).value
    const key = name ? `${name} [${this.id}]` : `Connector [${this.id}] info`
    const modalInstance = this.modalsStore.init(key, markRaw(AccountConnector), { allowTray: true })
    modalInstance?.open(this.modalsStore.getZIndex(), {
      instance: this,
      assetId,
    })
    callback && modalInstance?.addEventListener(ModalEvent.CLOSE, callback)
  }

  getFormType() {
    return AssetFormType
  }

  getFormName() {
    return AssetFormName
  }

  getFormTicker() {
    return AssetFormTicker
  }

  getFormCurrency() {
    return AssetFormCurrency
  }

  getFormDescription() {
    return AssetFormDescription
  }

  getFormCategory() {
    return AccountFormCategory
  }

  getFormSubcategory() {
    return AccountFormSubcategory
  }

  validate() {
    const result: Record<string, Rule> = {
      type: rules.required,
      name: rules.required,
      currency: rules.required,
      description: rules.limited,
    }
    if (this.isAccount) {
      result.tags = rules.accountTags
    } else {
      result.ticker = rules.required
    }
    return result
  }

  get currencySymbol(): string {
    return getCurrencySymbol(this.field<string>(ASSET_FIELD.CURRENCY).value)
  }

  get prices() {
    return this.priceList
  }

  set prices(values: AssetPrice[] | undefined) {
    this.priceList = values
  }

  get securityInfo() {
    return this.securityInfoValue
  }

  set securityInfo(values: AssetSecurityInfo | undefined) {
    this.securityInfoValue = values
  }

  private removeFromBunch() {
    this.bunchStore.getList.delete(this.id)
  }

  private removeFromForms() {
    this.modalsStore.getList.delete(this.id)
  }

  remove() {
    this.removeFromBunch()
    this.removeFromForms()
  }

  handleErrors(errors: EntityErrors) {
    return errors
  }

  forceUpdate(data: Asset) {
    super.forceUpdate({
      ...data,
      description: data.description ?? '',
    })
  }

  forceUpdateField(path: keyof Asset, value: any) {
    // Convert null description to empty string
    if (path === ASSET_FIELD.DESCRIPTION) value = value ?? ''

    super.forceUpdateField(path, value)
  }

  protected reset(data: Asset) {
    super.reset({
      ...data,
      description: data.description ?? '',
    })
  }

  async store() {
    try {
      const data = this.get()
      const result = await this.assetsStore.store(data)
      this.remove()
      if (result !== undefined) {
        this.reset(result)
        this.bunchStore.unshiftElement(this)
        this.callEvent(EntityEvent.STORED)
        const createdCurrency = this.field<Asset | undefined>(
          ASSET_FIELD.CURRENCY_ASSET,
        ).value
        if (createdCurrency) {
          const currencyAsset = new AssetClass(cloneDeep(createdCurrency))
          this.bunchStore.unshiftElement(currencyAsset)
        }
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async update() {
    try {
      const result = await this.assetsStore.update(this.get())
      if (result === undefined) {
        this.cancel()
      } else {
        this.callEvent(EntityEvent.UPDATED)
        this.reset(result)
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async destroy() {
    try {
      if (!this.isNew) {
        await this.assetsStore.destroy(this.id)
        this.callEvent(EntityEvent.DESTROYED)
      }
      this.remove()
    } catch (e) {
      throw Error(e as string)
    }
  }

  async assignTags(tagIds: string[], override = false) {
    this.frozen = true
    if (override) {
      this.field(ASSET_FIELD.TAGS).value = tagIds
    } else {
      this.field(ASSET_FIELD.TAGS).value = [...new Set([...this.field<string[]>(ASSET_FIELD.TAGS).value, ...tagIds])]
    }
    if (!this.isNew) {
      await this.update()
    }
    this.frozen = false
  }

  async unAssignTags(tagIds: string[]) {
    this.frozen = true
    this.field(ASSET_FIELD.TAGS).value = this.field<string[]>(ASSET_FIELD.TAGS).value.filter(tag => !tagIds.includes(tag))
    if (!this.isNew) {
      await this.update()
    }
    this.frozen = false
  }

  get displayTags() {
    const tagIds = this.field<string[]>(ASSET_FIELD.TAGS).value
    if (!tagIds || tagIds.length === 0) return []

    const tagsBunchStore = useTagsBunchStore()
    return tagIds
      .map(tagId => {
        const tag = tagsBunchStore.getElementById(tagId)
        return tag?.displayPair
      })
  }

  updatePrices(values: AssetPrice[], clear = false) {
    if (clear) {
      this.prices = values
    } else {
      this.prices = [...(this.prices || []), ...values]
    }
  }
}
